import {
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  effect,
  inject,
  viewChildren,
  input,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateRange } from '@angular/material/datepicker';
import {
  V2TextInputComponent,
  V2DateInputComponent,
  V2DateRangeInputComponent,
  V2DualPillComponent
} from '@coin/shared/core/ui-storybook';
import { ListViewFilterTagType } from '@coin/shared/core/util-enums';
import {
  ListViewFilterTag,
  ListViewFilterTagSubFilter,
  ListViewTagFilterParameter
} from '@coin/shared/core/util-models';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { DateRangeListViewFilterTag } from './date-range-filter-tag.model';
import { NgTemplateOutlet } from '@angular/common';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';

// TODO refactor whole class
@Component({
  selector: 'coin-c-list-view-filter-tag',
  templateUrl: './list-view-filter-tag.component.html',
  styleUrls: ['./list-view-filter-tag.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    V2TextInputComponent,
    FormsModule,
    V2DateInputComponent,
    ReactiveFormsModule,
    V2DateRangeInputComponent,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    V2DualPillComponent,
    TranslatePipe
  ]
})
export class ListViewFilterTagComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly tag = input<ListViewFilterTag>(undefined);
  public readonly selections = input<ListViewTagFilterParameter[]>(undefined);
  public readonly hideCount = input<boolean>(undefined);

  readonly textInputQueryList = viewChildren<V2TextInputComponent>('textInput');

  @Output() filterChanged = new EventEmitter<void>();
  @Output() clearFilter = new EventEmitter<void>();
  @Output() addFilter = new EventEmitter<ListViewTagFilterParameter>();
  @Output() removeFilter = new EventEmitter<ListViewTagFilterParameter>();

  public textInputComponent: V2TextInputComponent;
  public isInEditMode = false;
  public inputText: string;
  public selectedFilters: ListViewFilterTagSubFilter[] = [];
  public listViewFilterTagType = ListViewFilterTagType;
  public dateFormControl = new FormControl<Moment | undefined>(undefined);
  public dateRangeFormControl = new FormControl<DateRange<Moment> | null>(null);
  public isBinaryFilterActive = false;

  public get isBinary(): boolean {
    return this.tag().type === ListViewFilterTagType.Binary;
  }

  get showDropdownFilter(): boolean {
    const tag = this.tag();
    return !tag?.type || tag?.type === ListViewFilterTagType.DropdownWithoutTotalCount;
  }

  get tagValue(): string {
    const tag = this.tag();
    if (tag) {
      const title = this.tagTitle;

      if (this.isBinary) {
        return `${tag.count}` || '';
      }

      if (
        [
          ListViewFilterTagType.Input,
          ListViewFilterTagType.DropdownWithoutTotalCount,
          ListViewFilterTagType.Date,
          ListViewFilterTagType.DateRange
        ].includes(tag.type)
      ) {
        return title;
      }
      if (!this.selectedFilters.length) {
        return `${tag.count || ''}`;
      }
      const count = this.selectedFilters
        .map(filter => this.tag().subFilters?.find(subfilter => subfilter.type === filter.type).count)
        .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

      if (Number.isNaN(count) || !tag.count) {
        return title;
      }
      return `${title} ${count}/${tag.count}`;
    }
    return '';
  }

  private get tagTitle(): string {
    if (!this.selectedFilters.length) {
      return '';
    }
    if (this.selectedFilters.length === 1) {
      return this.translateService.instant(this.selectedFilters[0].title);
    }
    return `Selected: ${this.selectedFilters.length}`;
  }

  constructor() {
    this.dateFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.selectDate());
    this.dateRangeFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.selectDateRange());
    effect(() => {
      this.textInputQueryList().at(0)?.focus();
    });
    effect(() => {
      // Track tag and selections so we re-derive the local selectedFilters
      // whenever the parent's filter state changes.
      this.tag();
      this.selections();
      this.setSelectedFiltersFromInput();
    });
  }

  ngOnInit(): void {
    if (!this.selections()) {
      const selectedItem = this.tag()?.subFilters?.find(filter => filter.selected);
      if (selectedItem) {
        this.selectedFilters[0] = selectedItem;
        this.addFilter.emit({ category: this.category, value: selectedItem.type });
      }
    }
  }

  public isSelected(filter: ListViewFilterTagSubFilter): boolean {
    return this.selectedFilters.findIndex(selected => selected.type === filter.type) > -1;
  }

  public get category(): string {
    return this.tag()?.filterKey;
  }

  public onClearTextInput(): void {
    this.inputText = undefined;
  }

  public onFilterClicked(filter: ListViewFilterTagSubFilter): void {
    if (this.isSelected(filter)) {
      this.deselectFilter(filter);
    } else {
      this.selectFilter(filter);
    }
  }

  public selectFilter(filter: ListViewFilterTagSubFilter): void {
    if (this.tag().isMultiSelect) {
      this.selectedFilters.push(filter);
    } else {
      if (this.selectedFilters[0]) {
        this.removeFilter.emit({ category: this.category, value: this.selectedFilters[0].type, intermediate: true });
      }
      this.selectedFilters[0] = filter;
    }
    this.addFilter.emit({ category: this.category, value: filter.type });
  }

  public deselectFilter(filter: ListViewFilterTagSubFilter): void {
    if (filter) {
      this.selectedFilters = this.selectedFilters.filter(selectedFilter => selectedFilter.type !== filter.type);
      this.removeFilter.emit({ category: this.category, value: filter.type, intermediate: false });
    }
  }

  public toggleEditMode(): void {
    const tag = this.tag();
    if (tag.type !== ListViewFilterTagType.Binary) {
      this.isInEditMode = !this.isInEditMode;
    } else if (this.isBinaryFilterActive) {
      this.deselectFilter(tag.subFilters[0]);
      this.isBinaryFilterActive = false;
    } else {
      this.selectFilter(tag.subFilters[0]);
      this.isBinaryFilterActive = true;
    }
  }

  public reset(): void {
    this.selectedFilters = [];
  }

  public submitTextInput(): void {
    this.isInEditMode = false;
    this.toggleFilterValue(this.inputText);
  }

  public selectDate(): void {
    this.isInEditMode = false;
    this.toggleFilterValue(this.dateFormControl.value?.format('MM/DD/YYYY'));
  }

  public selectDateRange(): void {
    const range = this.dateRangeFormControl.value;
    if (!range?.start || !range?.end) return;
    this.isInEditMode = false;
    this.applyDateRange(range.start, range.end);
  }

  public onDateRangePickerClosed(): void {
    const range = this.dateRangeFormControl.value;
    this.isInEditMode = false;
    if (range?.start && !range.end) {
      // User picked only a start date; keep the end-date filter open
      // and leave the end input empty so it's clear nothing is bound to it.
      this.applyDateRange(range.start, null);
    }
  }

  private applyDateRange(start: Moment, end: Moment | null): void {
    const tag = this.tag() as DateRangeListViewFilterTag;
    const startKey = tag.filterKey;
    const endKey = tag.endFilterKey;
    const startValue = start.format('YYYY-MM-DD');
    const endValue = end ? end.format('YYYY-MM-DD') : null;

    const currentStart = this.selections()?.find(item => item.category === startKey);
    const currentEnd = endKey ? this.selections()?.find(item => item.category === endKey) : undefined;

    if (currentStart && currentStart.value !== startValue) {
      this.removeFilter.emit({ category: startKey, value: currentStart.value, intermediate: true });
    }
    if (currentEnd && currentEnd.value !== endValue) {
      this.removeFilter.emit({ category: endKey, value: currentEnd.value, intermediate: true });
    }

    if (!currentStart || currentStart.value !== startValue) {
      this.addFilter.emit({ category: startKey, value: startValue });
    }
    if (endKey && endValue && (!currentEnd || currentEnd.value !== endValue)) {
      this.addFilter.emit({ category: endKey, value: endValue });
    }

    const title = end ? `${start.format('MM/DD/YYYY')} - ${end.format('MM/DD/YYYY')}` : start.format('MM/DD/YYYY');
    this.selectedFilters = [{ title, type: startValue }];
  }

  private toggleFilterValue(value: string): void {
    if (value) {
      this.selectFilter({
        title: value,
        type: value
      });
    } else if (this.selectedFilters[0]) {
      this.deselectFilter(this.selectedFilters[0]);
    }
  }

  private setSelectedFiltersFromInput(): void {
    this.selectedFilters = [];
    this.isBinaryFilterActive = false;

    if (this.tag()?.type === ListViewFilterTagType.DateRange) {
      this.applyDateRangeSelection();
      return;
    }

    this.selections()
      ?.filter(selection => this.tag()?.subFilters?.length && selection.category === this.category)
      .forEach(selectedSubFilter => {
        const selectedItem = this.tag().subFilters.find(filter => filter.type === selectedSubFilter.value);
        if (selectedItem) {
          const updatedItem: ListViewFilterTagSubFilter = { ...selectedItem, selected: true };
          this.selectedFilters.push(updatedItem);
          if (this.isBinary) {
            this.isBinaryFilterActive = true;
          }
        }
      });
  }

  private applyDateRangeSelection(): void {
    const tag = this.tag() as DateRangeListViewFilterTag;
    const startSelection = this.selections()?.find(item => item.category === tag.filterKey);
    const endSelection = tag.endFilterKey
      ? this.selections()?.find(item => item.category === tag.endFilterKey)
      : undefined;

    if (!startSelection?.value) {
      this.dateRangeFormControl.setValue(null, { emitEvent: false });
      return;
    }

    const start = moment.utc(startSelection.value);
    const end = endSelection?.value ? moment.utc(endSelection.value) : null;
    const title = end ? `${start.format('MM/DD/YYYY')} - ${end.format('MM/DD/YYYY')}` : start.format('MM/DD/YYYY');
    this.selectedFilters.push({ title, type: startSelection.value });
    this.dateRangeFormControl.setValue(new DateRange<Moment>(start, end), { emitEvent: false });
  }
}
